// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Order from "./order.js"
import OrderGeneration from "./order-generation.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "OrderGenerationOrder",
  "collectionKey": "order_generation_orders",
  "collectionName": "order_generation_orders",
  "i18nKey": "order_generation_order",
  "camelizedLower": "orderGenerationOrder",
  "name": "OrderGenerationOrder",
  "nameDasherized": "order-generation-order",
  "pluralName": "order_generation_orders",
  "ransackable_associations": [
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Order",
      "collectionName": "orders",
      "foreignKey": "order_id",
      "name": "order",
      "macro": "belongs_to",
      "resource_name": "Order",
      "through": null
    },
    {
      "className": "OrderGeneration",
      "collectionName": "order_generations",
      "foreignKey": "order_generation_id",
      "name": "order_generation",
      "macro": "belongs_to",
      "resource_name": "OrderGeneration",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "order_generation_id",
      "column": {
        "default": null,
        "name": "order_generation_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "order_id",
      "column": {
        "default": null,
        "name": "order_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Order",
      "collectionName": "orders",
      "foreignKey": "order_id",
      "name": "order",
      "macro": "belongs_to",
      "resource_name": "Order",
      "through": null
    },
    {
      "className": "OrderGeneration",
      "collectionName": "order_generations",
      "foreignKey": "order_generation_id",
      "name": "order_generation",
      "macro": "belongs_to",
      "resource_name": "OrderGeneration",
      "through": null
    }
  ],
  "paramKey": "order_generation_order",
  "primaryKey": "id"
}

/** Frontend model for OrderGenerationOrder. */
export default class OrderGenerationOrder extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./order.js").default | null} */
  order() {
    return this._readBelongsToReflection({modelClass: Order, reflectionName: "order"})
  }

  /** @returns {Promise<import("./order.js").default | null>} */
  loadOrder() {
    if (!("orderId" in this)) throw new Error("Foreign key method wasn't defined: orderId")

    const id = this.orderId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "order",
        model: this,
        modelClass: Order
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./order.js").default | null>} */
  ensureOrderLoaded() {
    return this.ensureAssociationLoaded("order")
  }

  /** @returns {import("./order-generation.js").default | null} */
  orderGeneration() {
    return this._readBelongsToReflection({modelClass: OrderGeneration, reflectionName: "order_generation"})
  }

  /** @returns {Promise<import("./order-generation.js").default | null>} */
  loadOrderGeneration() {
    if (!("orderGenerationId" in this)) throw new Error("Foreign key method wasn't defined: orderGenerationId")

    const id = this.orderGenerationId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "order_generation",
        model: this,
        modelClass: OrderGeneration
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./order-generation.js").default | null>} */
  ensureOrderGenerationLoaded() {
    return this.ensureAssociationLoaded("order_generation")
  }
}
